import axios from 'axios';
import { Store } from 'vuex';
import * as Sentry from '@sentry/vue';
import VueRouter from 'vue-router';
import TranslationService from '@/locale/translation.service';
import PollingService from '@/shared/polling/polling.service';
import EntityFactory from '@/shared/entity-commons/entity.factory';
import { UsuariosApi } from '@/shared/openapi-premios-client';

export default class AccountService {
  private usuariosApi: () => UsuariosApi;
  private rolesValidos = EntityFactory.getRolesUsuarioValidos();

  constructor(
    private store: Store<any>,
    private translationService: TranslationService,
    private cookie: any,
    private router: VueRouter,
    private pollingService: PollingService
  ) {
    this.init();
  }

  public setUsuariosApi(usuariosApi: () => UsuariosApi): void {
    this.usuariosApi = usuariosApi;
  }

  public init(): void {
    this.retrieveProfiles();
  }

  public retrieveProfiles(): Promise<boolean> {
    return new Promise(resolve => {
      axios
        .get<any>('management/info')
        .then(res => {
          if (res.data && res.data.activeProfiles) {
            this.store.commit('setRibbonOnProfiles', res.data['display-ribbon-on-profiles']);
            this.store.commit('setActiveProfiles', res.data['activeProfiles']);
          }
          resolve(true);
        })
        .catch(() => resolve(false));
    });
  }

  public retrieveAccount(): Promise<boolean> {
    return new Promise(resolve => {
      axios
        .get<any>('api/account')
        .then(async response => {
          this.store.commit('authenticate');

          const accountTemp = response.data;
          try {
            if (this.usuariosApi) {
              const { data: usuario } = await this.usuariosApi().getUsuarioByCvu(response.data.cvu);
              // Si hay usuario y tiene roles, fusionarlos
              if (usuario?.roles?.length) {
                const rolesUsuario = usuario.roles.map(r => r.rol);
                accountTemp.authorities = Array.from(new Set([...(accountTemp.authorities || []), ...rolesUsuario]));
              }
            }
          } catch (error) {
            console.warn('No se pudo obtener roles adicionales:', error);
          }

          const account = accountTemp;

          if (account) {
            Sentry.configureScope(scope => {
              scope.setUser({ email: account.email });
            });
            this.pollingService.init();
            // Intentar leer y decodificar el rol guardado
            const stored = localStorage.getItem('itemUserKey');
            let selectedRoleFromStorage: string | null = null;
            if (stored) {
              try {
                selectedRoleFromStorage = window.atob(stored);
              } catch {
                selectedRoleFromStorage = null;
              }
            }

            const rolesValidos = EntityFactory.getRolesUsuarioValidos();
            const authorities: string[] = Array.isArray(account?.authorities) ? account.authorities : [];
            const authoritiesValidas = authorities.filter(a => rolesValidos.includes(a));

            if (selectedRoleFromStorage && authoritiesValidas.includes(selectedRoleFromStorage)) {
              // El rol guardado SÍ pertenece al usuario actual → mantenerlo
              this.store.commit('setSelectedRole', selectedRoleFromStorage);
            } else {
              // Limpiar rastro del usuario anterior o rol inválido
              this.store.commit('setSelectedRole', null);
              localStorage.removeItem('itemUserKey');
            }

            this.store.commit('authenticated', account);
            if (this.store.getters.currentLanguage !== account.langKey) {
              this.store.commit('currentLanguage', account.langKey);
            }
            if (sessionStorage.getItem('requested-url')) {
              this.router.replace(sessionStorage.getItem('requested-url'));
              sessionStorage.removeItem('requested-url');
            }
          } else {
            // Sin cuenta → logout y limpieza
            this.store.commit('setSelectedRole', null);
            this.store.commit('logout');

            if (this.router.currentRoute.path !== '/') {
              this.router.push('/');
            }
            sessionStorage.removeItem('requested-url');
          }
          this.translationService.refreshTranslation(this.store.getters.currentLanguage);
          resolve(true);
        })
        .catch(() => {
          this.store.commit('setSelectedRole', null); // asegurar limpieza también en error
          this.store.commit('logout');
          resolve(false);
        });
    });
  }

  public hasAnyAuthorityAndCheckAuth(authorities: any): Promise<boolean> {
    if (typeof authorities === 'string') {
      authorities = [authorities];
    }

    if (!this.authenticated || !this.userAuthorities) {
      const token = this.cookie.get('JSESSIONID') || this.cookie.get('XSRF-TOKEN');
      if (!this.store.getters.account && !this.store.getters.logon && token) {
        return this.retrieveAccount().then(resp => {
          if (resp) {
            return this.checkAuthorities(authorities);
          }
          return Promise.resolve(false);
        });
      }
      return Promise.resolve(false);
    }

    return this.checkAuthorities(authorities);
  }

  public get authenticated(): boolean {
    return this.store.getters.authenticated;
  }

  public get userAuthorities(): any {
    return this.store.getters.account?.authorities;
  }

  private checkAuthorities(authorities: any): Promise<boolean> {
    if (this.userAuthorities) {
      for (const authority of authorities) {
        if (this.userAuthorities.includes(authority)) {
          return Promise.resolve(true);
        }
      }
    }
    return Promise.resolve(false);
  }

  public checkAuthoritiesExistMoreThanOne(): boolean {
    if (this.userAuthorities != undefined) {
      const rolesValidos = this.rolesValidos.filter(elemento => this.userAuthorities.includes(elemento));
      if (rolesValidos.length > 1) {
        return true;
      }
    }
    return false;
  }

  public actualizarRol(rol: string): void {
    const authorities = this.store.getters.account?.authorities ?? [];
    const rolesValidos = EntityFactory.getRolesUsuarioValidos();
    if (authorities.includes(rol) && rolesValidos.includes(rol)) {
      this.store.commit('setSelectedRole', rol);
      localStorage.setItem('itemUserKey', window.btoa(rol));
    } else {
      // Rol inválido para este usuario -> limpiar por seguridad
      this.store.commit('setSelectedRole', null);
      localStorage.removeItem('itemUserKey');
    }
  }
}
